import { useState, useMemo, useCallback } from 'react';
import { AttorneyData } from '../../../dtos/applicationData';
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter';
import { useRenewal } from '../../../context/renewal';
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
} from '../../../components/application/defaultComponents';
import NewAttorneysForm from 'src/components/application/mainPagesComponents/Attorneys/NewForm';
import { FlexDiv, FormInputErrorMessage } from 'src/components/application/formComponents/styles';
import OptionButton from 'src/components/basic/OptionButton';
import { FirmAttorneysJoinedSchema } from 'src/yup/RenewalSchema';
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage';
import { ValidationError } from 'yup';

const newAttorneyData: AttorneyData = {
  firstName: '',
  lastName: '',
  barCardNumber: '',
  outSideTexasBarCardNumber: '',
  position: '',
  workHistory: {
    joinedDate: '',
    hoursPerWeek: undefined,
  },
  emailAddress: '',
  yearsInPrivatePractice: undefined,
  license: {
    isLicensedInTexas: undefined,
    licenseDate: '',
    isLicensedInOtherState: undefined,
    licensesInfo: '',
    dateLicensedInOtherState: '',
    otherState: '',
    percentageOfWorkSpent: '',
  }
};

const Main = () => {
  const { renewalData, saveRenewalData } = useRenewal();
  const [attorneys, setAttorneys] = useState<AttorneyData[]>(() => {
    return renewalData?.renewalSection?.newAttorneys ?? [];
  });
  const [hasNewAttorneys, setHasNewAttorneys] = useState<boolean | undefined>(renewalData?.renewalSection?.hasNewAttorneys);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [, setDisableSaveandContinue] = useState(false);

  /*
      code,data,array-position
  */
  const newAttorneyErrorCodes: string[] = [
    'EC32', // EC32 - First name - 0
    'EC33', // EC33 - Last Name - 1
    'EC34', // EC34 - BarCardNumber - 2
    'EC35', // EC35 - Position - 3
    'EC36', // EC36 - Email - 4
    'EC37', // EC37 - yearsInPrivatePractice - 5
    'EC38', // EC38 - LicenseDate - 6
    'EC39', // EC39 - IsLicensedInOtherState - 7
    'EC40', // EC40 - OtherState - 8
    'EC41', // EC41 - DateLicensedInOtherState - 9
    'EC42', // EC42 - PercentageOfWorkSpent - 10
    'EC43', // EC43 - LicensesInfo - 11
    'EC44', // EC44 - JoinedDate - 12
    'EC45', // EC45 - HoursPerWeek - 13
    'EC31', // EC31 - HasNewAttorneys - 14
    'EC47',
    'EC48',
    'EC49',
    'EC321',
    'EC322',
    'EC323',
    'EC324',
    'EC325',
    'EC326', // EC326 - at least one, IsLicensedInOtherState or isLicensedInTexas needs to be true - 23
    'EC327'  // EC327 - OutSideTexasBarCardNumber - 24
  ];

  const validateAttorney = useCallback((attorney: AttorneyData) => {
    try {
      FirmAttorneysJoinedSchema.validateSync(attorney);
      return true;
    } catch (e) {
      const { errors } = e as ValidationError;
      return false;
    }
  }, []);

  const validationPerAttorney = useMemo(() => {
    let validatedAttorney: { [key: string]: boolean } = {};

    for (let index = 1; index <= attorneys.length; index++) {
      validatedAttorney[`att__${index}`] = validateAttorney(attorneys[index - 1]);
    }

    return validatedAttorney;
  }, [attorneys, validateAttorney]);

  const updatedApplicationData = useMemo(() => ({

    ...renewalData,
    currentPage: window.location.pathname,
    stoppedAt: '/customer/renew/attorneys/joined',
    renewalSection: {
      ...renewalData.renewalSection,
      newAttorneys: attorneys.length > 0 ? attorneys : undefined,
      hasNewAttorneys,
    },
  }), [attorneys, hasNewAttorneys, renewalData]);

  const goBackRoute = useMemo(() => '/customer/renew/attorneys/left', []);

  const handleScrollOnMissingData = useCallback(() => {
    if (hasNewAttorneys === undefined || hasNewAttorneys === null) {
      document.getElementById('new_attorneys_main_question')?.scrollIntoView({ behavior: 'smooth' });
      setDisplayErrors(true);
    } else {
      for (let [key, isValid] of Object.entries(validationPerAttorney)) {
        if (!isValid) {
          document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' });
          setDisplayErrors(true);
          break;
        }
      }
    }
  }, [hasNewAttorneys, validationPerAttorney]);

  const pageValidation = useMemo(() => {
    if (hasNewAttorneys === undefined || hasNewAttorneys === null) {return false;}
    if (hasNewAttorneys === false) {return true;}
    return Object.keys(validationPerAttorney).every(key => validationPerAttorney[key] === true);
  }, [hasNewAttorneys, validationPerAttorney]);

  const handleIsFormValid = useCallback(() => {
    if (!hasNewAttorneys) {
      setDisableSaveandContinue(false);
      return true;
    }

    let isValid = pageValidation;
    if (isValid) {
      setDisableSaveandContinue(false);
    } else {
      setDisableSaveandContinue(true);
    }
  }, [hasNewAttorneys, pageValidation]);

  const handleSaveDetails = useCallback(async () => {
    handleIsFormValid();
    await saveRenewalData(updatedApplicationData);
  }, [handleIsFormValid, saveRenewalData, updatedApplicationData]);

  const onMainQuestionChange = useCallback((option: boolean) => {
    setHasNewAttorneys(option);
    setDisplayErrors(false);

    if (option === false) {
      setAttorneys([]);
    } else {
      
      setAttorneys(
        renewalData?.renewalSection?.newAttorneys && renewalData?.renewalSection?.newAttorneys.length > 0
          ? renewalData?.renewalSection?.newAttorneys
          : [newAttorneyData]
      );
    }
  }, [renewalData?.renewalSection?.newAttorneys]);

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle id="new_attorneys_main_question">
          {hasNewAttorneys === true ? 'Let’s update your firm’s attorneys' : 'Have any attorneys joined your firm in the last 12 months?'}
        </DefaultTitle>
        <DefaultSubtitle>
          {hasNewAttorneys === true
            ? 'Add attorneys who have joined your firm (including independent contractor and/or of counsel) in the last 12 months.'
            : <>Please include all independent contractors and of counsel attorneys added since your last application.</>}
        </DefaultSubtitle>
        <FlexDiv gap="16px" height='fit-content' margin='0 0 24px'>
          <OptionButton
            checked={hasNewAttorneys === true}
            onChange={() => onMainQuestionChange(true)}
            isIncorrect={displayErrors && (hasNewAttorneys === undefined || hasNewAttorneys === null)}
            errorCode={newAttorneyErrorCodes[14]}
          >
            Yes
          </OptionButton>
          <OptionButton
            checked={hasNewAttorneys === false}
            onChange={() => onMainQuestionChange(false)}
            isIncorrect={displayErrors && (hasNewAttorneys === undefined || hasNewAttorneys === null)}
            errorCode={newAttorneyErrorCodes[14]}
          >
            No
          </OptionButton>
        </FlexDiv>
        <ValidationErrorMessage errorCode={newAttorneyErrorCodes[14]} />
        {displayErrors && (hasNewAttorneys === undefined || hasNewAttorneys === null) && (
          <FormInputErrorMessage>
            This is a required field
          </FormInputErrorMessage>
        )}
        {hasNewAttorneys === true && (
          <NewAttorneysForm
            attorneys={attorneys}
            setAttorneys={setAttorneys}
            handleSaveDetails={handleSaveDetails}
            showErrors={displayErrors}
            setDisableSaveandContinue={setDisableSaveandContinue}
            isAttorneyVerification={false}
            errorCodes={newAttorneyErrorCodes}
          />
        )}
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute}
        isFormValid={pageValidation}
        nextRoute="/customer/renew/attorneys/verify"
        updatedApplication={updatedApplicationData}
        beforeSaveContinueClick={handleScrollOnMissingData}
      />
    </ApplicationBodyContainer>
  );
};

export default Main;
